/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import { parseCFApiParamKey } from 'volta/models/custom-field-definition';
import { indexByFn } from 'volta/utils/array-utils';
import { sanitized } from 'volta/utils/text-utils';

import { service } from '@ember/service';
import Component from '@glimmer/component';
import { cached, tracked } from '@glimmer/tracking';

import type { IntlService } from 'ember-intl';
import type { IResourceName } from 'volta/models/base-model';
import type { ICollectionFilter } from 'volta/models/types/collection-view';
import type LabelService from 'volta/services/label-service';

interface CollectionViewFiltersSignature {
  Args: {
    filters: TFilterDefinition[];
    filterValues: ICollectionFilter[];
    initialValue: unknown;
    resourceName: IResourceName;
    onChange: (filterValue: TFilterValue, queryParam: string) => void;
  };
}

export default class CollectionViewFiltersComponent extends Component<CollectionViewFiltersSignature> {
  @service intl!: IntlService;
  @service labelService!: LabelService;

  @tracked search?: string;

  get filtersWithTitle() {
    return this.args.filters.reduce((acc: (TFilterDefinition & { title: string })[], p) => {
      const labelKey =
        (p.customFieldDefinition && parseCFApiParamKey(p.queryParam)) || p.queryParam;
      const title = (
        p.customFieldDefinition
          ? this.labelService.tOrElse(labelKey, p.title)
          : (p.title ??
            this.labelService.tOrElse(
              p.queryParam && 'filterDefinitions.' + p.queryParam,
              this.intl.t(p.queryParam)
            ))
      ) as string;
      if (!p.hidden) {
        acc.push({ ...p, title });
      }

      return acc;
    }, []);
  }

  @cached
  get filteredFilters() {
    const { search, filtersWithTitle } = this;
    if (!search || search === '') {
      return filtersWithTitle;
    }
    const _search = sanitized(search);
    return filtersWithTitle.filter((p) => {
      return p.title && sanitized(p.title as string).includes(_search);
    });
  }

  @cached
  get indexedFilterValues() {
    return indexByFn(this.args.filterValues, (f) => f.property);
  }

  // helpers
  initialValue(filters: ICollectionFilter[] = [], queryParam: string) {
    return filters.find((filter: ICollectionFilter) => filter.property === queryParam);
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'CollectionView::Filters': typeof CollectionViewFiltersComponent;
    'collection-view/filters': typeof CollectionViewFiltersComponent;
  }
}
